import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DeactivateUserConfirmModal from '../../components/ui/DeactivateUserConfirmModal';
import ReactivateUserConfirmModal from '../../components/ui/ReactivateUserConfirmModal';
import { workerService } from '../../services/workerService';
import { companyService } from '../../services/companyService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';
import { useLanguage } from '../../contexts/LanguageContext';

// jyyb8AfJ PR 2 — 4-state taxonomy:
//   - deactivated: deactivated_at IS NOT NULL (admin revoked access)
//   - active:      is_active=true (completed invite setup, signs in)
//   - pending:     is_active=false AND invited_by IS NOT NULL (mid-invite)
//   - imported:    is_active=false AND invited_by IS NULL (CSV placeholder)
// "deactivated" wins over "active" because a deactivated user's is_active
// flag still reads true (PR 1 audit: is_active means "completed setup",
// not "currently has access").
const computeWorkerStatus = (worker) => {
  if (worker?.deactivated_at != null) return 'deactivated';
  if (worker?.is_active === true) return 'active';
  if (worker?.invited_by != null) return 'pending';
  return 'imported';
};

const WorkersList = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  // Default to "Active" so newly-added deactivated/pending/imported users
  // don't change what an admin sees on first load.
  const [statusFilter, setStatusFilter] = useState('active');
  // jyyb8AfJ — admin actions
  const [companyAdminUserId, setCompanyAdminUserId] = useState(null);
  const [actionModal, setActionModal] = useState(null); // { mode: 'deactivate'|'reactivate', user }
  const [actionInFlight, setActionInFlight] = useState(false);
  const [actionError, setActionError] = useState('');
  const isAdminViewer = userProfile?.role === 'admin';

  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    try {
      setLoading(true);
      // 9i4dXP36: route through getCompanyUsersWithMeta so each row carries
      // its assigned sites and auth.users.last_sign_in_at in one round-trip.
      // The RPC enforces caller-must-be-admin-or-site-manager-of-this-company,
      // so company_id is mandatory.
      const companyResult = await companyService?.getUserCompany();
      const companyId = companyResult?.data?.id;
      if (!companyId) {
        setWorkers([]);
        setError('No company context — cannot load users.');
        return;
      }
      // Capture for the per-row primary-admin check — we never expose a
      // deactivate button against the company's admin_user_id even though
      // the edge fn also rejects it server-side.
      setCompanyAdminUserId(companyResult?.data?.admin_user_id || null);
      const result = await workerService?.getCompanyUsersWithMeta(companyId);
      if (result?.error) throw result.error;
      setWorkers(result?.data);
    } catch (err) {
      console.error('[WorkersList] load workers failed:', err);
      setError('Failed to load workers. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkers = useMemo(() => {
    let result = [...workers];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      result = result?.filter(worker =>
        worker?.full_name?.toLowerCase()?.includes(searchLower) ||
        worker?.email?.toLowerCase()?.includes(searchLower)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      result = result?.filter(worker => worker?.role === roleFilter);
    }

    // Status filter — uses the 4-state taxonomy from computeWorkerStatus
    if (statusFilter !== 'all') {
      result = result?.filter(worker => computeWorkerStatus(worker) === statusFilter);
    }

    return result;
  }, [workers, searchTerm, roleFilter, statusFilter]);

  const summaryStats = useMemo(() => {
    const totalWorkers = workers?.length || 0;
    // "Active users" now means computeWorkerStatus === 'active' so the
    // count doesn't include deactivated rows (which still carry
    // is_active=true on the column).
    const activeWorkers = workers?.filter(w => computeWorkerStatus(w) === 'active')?.length || 0;
    const adminCount = workers?.filter(w => w?.role === 'admin')?.length || 0;
    const siteManagerCount = workers?.filter(w => w?.role === 'site_manager')?.length || 0;
    const workerCount = workers?.filter(w => w?.role === 'worker')?.length || 0;
    const clientCount = workers?.filter(w => w?.role === 'client')?.length || 0;

    return { totalWorkers, activeWorkers, adminCount, siteManagerCount, workerCount, clientCount };
  }, [workers]);

  const handleWorkerClick = (workerId) => {
    navigate(`/workers/${workerId}`);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchTerm || roleFilter !== 'all' || statusFilter !== 'all';

  const roleLabels = {
    admin: 'Admin',
    site_manager: 'Site Manager',
    worker: 'Worker',
    client: 'Client',
  };

  const formatRole = (role) => roleLabels[role] || 'N/A';

  // 9i4dXP36 — assigned-sites column display.
  // - admin: RPC returns NULL → "All sites" (company-wide).
  // - non-admin with empty array: "No sites".
  // - 1-2 sites: list names.
  // - 3+ sites: "Site A, Site B, +N more".
  const formatAssignedSites = (worker) => {
    if (worker?.role === 'admin' || worker?.assigned_site_names == null) {
      return t('allSitesLabel');
    }
    const names = worker?.assigned_site_names || [];
    if (names?.length === 0) return t('noSitesLabel');
    if (names?.length <= 2) return names?.join(', ');
    const more = names?.length - 2;
    return `${names?.slice(0, 2)?.join(', ')}, ${t('moreSitesSuffix')?.replace('{count}', more)}`;
  };

  // 9i4dXP36 — last-logged-in column display.
  // Keep the format consistent with the existing Joined cell (locale date).
  // Null → "Never" (user invited but has never signed in).
  const formatLastLoggedIn = (lastSignInAt) => {
    if (!lastSignInAt) return t('neverLoggedInLabel');
    return new Date(lastSignInAt)?.toLocaleDateString();
  };

  const roleFilterOptions = [
    { value: 'all', label: t('allRoles') },
    { value: 'admin', label: 'Admin' },
    { value: 'site_manager', label: 'Site Manager' },
    { value: 'worker', label: 'Worker' },
    { value: 'client', label: 'Client' },
  ];

  const statusFilterOptions = [
    { value: 'all', label: t('allStatus') },
    { value: 'active', label: t('active') },
    { value: 'pending', label: t('pending') },
    { value: 'deactivated', label: 'Deactivated' },
    { value: 'imported', label: 'Imported' },
  ];

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-destructive/10 text-destructive';
      case 'site_manager':
        return 'bg-purple-100 text-purple-700';
      case 'worker':
        return 'bg-accent/10 text-accent';
      case 'client':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusBadge = (worker) => {
    const status = computeWorkerStatus(worker);
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
          <Icon name="CheckCircle2" size={12} />
          {t('active')}
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <Icon name="Clock" size={12} />
          {t('pending')}
        </span>
      );
    }
    if (status === 'deactivated') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
          <Icon name="UserX" size={12} />
          Deactivated
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
        <Icon name="UserMinus" size={12} />
        Imported
      </span>
    );
  };

  // jyyb8AfJ — only admin viewers see deactivate/reactivate actions, and
  // only on non-self / non-primary-admin rows. Edge fn also enforces all
  // three, but hiding the UI avoids the 403 motion entirely.
  const canShowDeactivate = (worker) => (
    isAdminViewer
      && computeWorkerStatus(worker) === 'active'
      && worker?.id !== userProfile?.id
      && worker?.id !== companyAdminUserId
  );
  const canShowReactivate = (worker) => (
    isAdminViewer
      && computeWorkerStatus(worker) === 'deactivated'
      && worker?.id !== userProfile?.id
  );

  const openDeactivate = (worker) => {
    setActionError('');
    setActionModal({ mode: 'deactivate', user: worker });
  };
  const openReactivate = (worker) => {
    setActionError('');
    setActionModal({ mode: 'reactivate', user: worker });
  };
  const closeActionModal = () => {
    if (actionInFlight) return;
    setActionModal(null);
    setActionError('');
  };
  const handleConfirmAction = async () => {
    if (!actionModal?.user?.id) return;
    setActionInFlight(true);
    setActionError('');
    try {
      const userId = actionModal.user.id;
      const result = actionModal.mode === 'deactivate'
        ? await userService?.deactivateUser(userId)
        : await userService?.reactivateUser(userId);
      if (!result?.success) {
        setActionError(result?.error || 'Action failed');
        return;
      }
      setActionModal(null);
      await loadWorkers();
    } finally {
      setActionInFlight(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <div className="h-8 bg-muted rounded w-48 mb-2 animate-pulse" />
            <div className="h-4 bg-muted rounded w-64 mb-6 animate-pulse" />
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    {['Name', 'Email', 'Role', 'Status', 'Assigned Sites', 'Last Logged In', 'Actions']?.map((h) => (
                      <th key={h} className="px-6 py-4 text-left text-sm font-semibold text-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <Skeleton variant="table-row" count={6} cols={7} />
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(userProfile?.role === 'site_manager' ? '/site-manager-dashboard' : '/admin-dashboard')}
                iconName="ArrowLeft"
              >
                {t('back')}
              </Button>
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground font-heading">{t('usersTeams')}</h1>
            <p className="text-muted-foreground mt-1">{t('usersTeamsSubtitle')}</p>
          </div>

          <div className="flex justify-end mb-4">
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/invite-users')}
              iconName="UserPlus"
            >
              {t('inviteTeamMembers')}
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <p className="text-sm text-muted-foreground font-caption mb-1">{t('totalUsers')}</p>
              <p className="text-2xl font-semibold text-foreground font-heading">{summaryStats?.totalWorkers}</p>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <p className="text-sm text-muted-foreground font-caption mb-1">{t('activeUsers')}</p>
              <p className="text-2xl font-semibold text-success font-heading">{summaryStats?.activeWorkers}</p>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <p className="text-sm text-muted-foreground font-caption mb-1">{t('admins')}</p>
              <p className="text-2xl font-semibold text-destructive font-heading">{summaryStats?.adminCount}</p>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <p className="text-sm text-muted-foreground font-caption mb-1">{t('siteManagers')}</p>
              <p className="text-2xl font-semibold text-purple-600 font-heading">{summaryStats?.siteManagerCount}</p>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <p className="text-sm text-muted-foreground font-caption mb-1">{t('workers')}</p>
              <p className="text-2xl font-semibold text-accent font-heading">{summaryStats?.workerCount}</p>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <p className="text-sm text-muted-foreground font-caption mb-1">{t('clients')}</p>
              <p className="text-2xl font-semibold text-success font-heading">{summaryStats?.clientCount}</p>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-card rounded-lg p-4 lg:p-6 shadow-elevation-2 border border-border mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  icon="Search"
                />
              </div>
              <div className="w-full lg:w-48">
                <Select
                  value={roleFilter}
                  onChange={(value) => setRoleFilter(value)}
                  options={roleFilterOptions}
                />
              </div>
              <div className="w-full lg:w-48">
                <Select
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value)}
                  options={statusFilterOptions}
                />
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  iconName="X"
                >
                  {t('clearFilters')}
                </Button>
              )}
            </div>
            {hasActiveFilters && (
              <div className="mt-3 flex items-center gap-2">
                <Icon name="Filter" size={16} color="var(--color-accent)" />
                <span className="text-sm text-accent font-medium">{t('activeFiltersApplied')}</span>
              </div>
            )}
          </div>

          {/* Workers Table */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <p className="text-destructive font-medium">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadWorkers}
                className="mt-3"
              >
                {t('retry')}
              </Button>
            </div>
          )}

          {filteredWorkers?.length === 0 ? (
            <div className="bg-card rounded-lg shadow-elevation-2 border border-border">
              <EmptyState
                icon="Users"
                title={hasActiveFilters ? t('noWorkersMatch') : t('noTeamMembersYet')}
                description={hasActiveFilters ? t('noWorkersMatchDesc') : t('noTeamMembersDesc')}
                ctaLabel={!hasActiveFilters ? t('inviteTeamMembers') : t('clearFilters')}
                ctaAction={!hasActiveFilters ? () => navigate('/invite-users') : handleClearFilters}
                ctaIcon={!hasActiveFilters ? 'UserPlus' : 'X'}
              />
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-elevation-2 border border-border overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">{t('nameHeader')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">{t('emailHeader')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">{t('roleHeader')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">{t('statusHeader')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">{t('assignedSitesHeader')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">{t('lastLoggedInHeader')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">{t('actionsHeader')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredWorkers?.map((worker) => (
                      <tr
                        key={worker?.id}
                        className="hover:bg-muted/50 transition-smooth cursor-pointer"
                        onClick={() => handleWorkerClick(worker?.id)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-full">
                              <Icon name="User" size={20} color="var(--color-accent)" />
                            </div>
                            <p className="font-medium text-foreground">{worker?.full_name || 'N/A'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-foreground">{worker?.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(worker?.role)}`}>
                            {formatRole(worker?.role)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(worker)}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-foreground">
                            {formatAssignedSites(worker)}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-foreground whitespace-nowrap">
                            {formatLastLoggedIn(worker?.last_sign_in_at)}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e?.stopPropagation();
                                handleWorkerClick(worker?.id);
                              }}
                              iconName="ArrowRight"
                              iconPosition="right"
                            >
                              {t('view')}
                            </Button>
                            {canShowDeactivate(worker) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  openDeactivate(worker);
                                }}
                                iconName="UserX"
                                iconPosition="left"
                              >
                                Deactivate
                              </Button>
                            )}
                            {canShowReactivate(worker) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  openReactivate(worker);
                                }}
                                iconName="UserCheck"
                                iconPosition="left"
                              >
                                Reactivate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-border">
                {filteredWorkers?.map((worker) => (
                  <div
                    key={worker?.id}
                    className="p-4 cursor-pointer hover:bg-muted/50 transition-smooth"
                    onClick={() => handleWorkerClick(worker?.id)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-full flex-shrink-0">
                          <Icon name="User" size={20} color="var(--color-accent)" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-foreground truncate">{worker?.full_name || 'N/A'}</h4>
                          <p className="text-sm text-muted-foreground truncate">{worker?.email}</p>
                        </div>
                      </div>
                      {getStatusBadge(worker)}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground font-caption mb-1">Role</p>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(worker?.role)}`}>
                          {worker?.role || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground font-caption mb-1">{t('lastLoggedInHeader')}</p>
                        <p className="text-sm text-foreground">
                          {formatLastLoggedIn(worker?.last_sign_in_at)}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground font-caption mb-1">{t('assignedSitesHeader')}</p>
                        <p className="text-sm text-foreground">
                          {formatAssignedSites(worker)}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleWorkerClick(worker?.id);
                        }}
                        iconName="ArrowRight"
                        iconPosition="right"
                        fullWidth
                      >
                        {t('viewDetails')}
                      </Button>
                      {canShowDeactivate(worker) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            openDeactivate(worker);
                          }}
                          iconName="UserX"
                          iconPosition="left"
                          fullWidth
                        >
                          Deactivate
                        </Button>
                      )}
                      {canShowReactivate(worker) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            openReactivate(worker);
                          }}
                          iconName="UserCheck"
                          iconPosition="left"
                          fullWidth
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <DeactivateUserConfirmModal
        isOpen={actionModal?.mode === 'deactivate'}
        onClose={closeActionModal}
        onConfirm={handleConfirmAction}
        userName={actionModal?.user?.full_name || actionModal?.user?.email}
        isDeactivating={actionInFlight}
      />
      <ReactivateUserConfirmModal
        isOpen={actionModal?.mode === 'reactivate'}
        onClose={closeActionModal}
        onConfirm={handleConfirmAction}
        userName={actionModal?.user?.full_name || actionModal?.user?.email}
        isReactivating={actionInFlight}
      />
      {actionError && actionModal && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-modal bg-destructive text-destructive-foreground rounded-lg shadow-elevation-5 px-4 py-3 max-w-md">
          <div className="flex items-start gap-2">
            <Icon name="AlertCircle" size={18} className="flex-shrink-0 mt-0.5" />
            <div className="text-sm">{actionError}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkersList;